import * as Sentry from "@sentry/nextjs";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import React, { FC } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { HeartIconOutlined } from "@/features/common/components/icons/HeartIconOutlined";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useHandleGraphqlError } from "@/features/common/hooks/useHandleGraphqlError";
import { updateAfterLike } from "@/features/post/components/action/postActionHelper";
import { useCreateLikeMutation } from "@/generated/generated-hooks";

type ImageCardLikeButtonProps = {
  postId: string;
  logEvent?: AnalyticsEventEnum;
};

export const ImageCardLikeButton: FC<
  ImageCardLikeButtonProps &
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >
> = (props) => {
  const { postId, children, logEvent } = props;
  const { openSnackbar } = useSnackbar();
  const { t } = useTranslation("common");
  const handleGraphqlError = useHandleGraphqlError();

  const [createLike, { loading }] = useCreateLikeMutation({
    variables: { input: postId },
    update: updateAfterLike({ postId, variables: { input: postId } }),
  });

  const onClickLike = async () => {
    if (loading) return;
    try {
      firebase
        .analytics()
        .logEvent(logEvent ?? AnalyticsEventEnum.ImageCardClickLike, {
          postId,
        });
      await createLike();
    } catch (e) {
      handleGraphqlError({
        e,
        sentryCaptureContext: {
          tags: {
            fn: "ImageCardLikeButton",
          },
        },
      });
    }
  };

  return (
    <>
      <div className="flex touchable" onClick={onClickLike}>
        {children ? (
          children
        ) : (
          <HeartIconOutlined
            className="text-gray-500 w-4 h-4"
            alt="like icon"
          />
        )}
      </div>
    </>
  );
};
